'use client';

import { c as _c } from "react/compiler-runtime";
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import React, { Suspense } from 'react';
import type { BlockProps, ComponentBaseProps } from '@sola/schema/lib/schema.ts';
import { AdditionalSettingsArticleList } from '@sola/schema/lib/schema.ts';
import { extractArgsSearchResult } from '@sola/skippy-search-sdk/lib/logic.ts';
import { useListArticlesQuery } from '@sola/skippy-search-sdk/lib/queries.ts';
import { FeaturedArticleCardSkeleton } from '../../../molecules/feature-article-card/feature-article-card-skeleton.tsx';
import { FeaturedArticleCard } from '../../../molecules/feature-article-card/feature-article-card.tsx';
const queryClient = new QueryClient();
export type ArticleListProfoundProps = BlockProps & ComponentBaseProps;
const ArticleListProfoundSkeleton = () => {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <section className="component component--no-header"><div className="container"><FeaturedArticleCardSkeleton /></div></section>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
};
export const ArticleListProfound: React.FC<ArticleListProfoundProps> = t0 => {
  const $ = _c(22);
  const {
    seoLevel: t1,
    settings
  } = t0;
  const seoLevel = t1 === undefined ? 3 : t1;
  let t2;
  if ($[0] !== settings) {
    t2 = settings ? AdditionalSettingsArticleList.parse(settings) : undefined;
    $[0] = settings;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const articleListSettings = t2;
  const t3 = articleListSettings?.topic;
  const t4 = articleListSettings?.theme;
  const t5 = articleListSettings?.country;
  let t6;
  if ($[2] !== t3 || $[3] !== t4 || $[4] !== t5) {
    t6 = {
      topic: t3,
      theme: t4,
      country: t5
    };
    $[2] = t3;
    $[3] = t4;
    $[4] = t5;
    $[5] = t6;
  } else {
    t6 = $[5];
  }
  const allFilters = t6;
  const t7 = articleListSettings?.articleType || undefined;
  let t8;
  if ($[6] !== allFilters.topic) {
    t8 = allFilters.topic ? [allFilters.topic] : undefined;
    $[6] = allFilters.topic;
    $[7] = t8;
  } else {
    t8 = $[7];
  }
  let t9;
  if ($[8] !== allFilters.theme) {
    t9 = allFilters.theme ? [allFilters.theme] : undefined;
    $[8] = allFilters.theme;
    $[9] = t9;
  } else {
    t9 = $[9];
  }
  const t10 = allFilters.country || undefined;
  const t11 = articleListSettings?.limit || 1;
  let t12;
  if ($[10] !== t10 || $[11] !== t11 || $[12] !== t7 || $[13] !== t8 || $[14] !== t9) {
    t12 = {
      promoted: true,
      articleType: t7,
      topics: t8,
      themes: t9,
      country: t10,
      perPage: t11,
      sort: "datePublished",
      sortDir: "desc"
    };
    $[10] = t10;
    $[11] = t11;
    $[12] = t7;
    $[13] = t8;
    $[14] = t9;
    $[15] = t12;
  } else {
    t12 = $[15];
  }
  const {
    data,
    isLoading
  } = useListArticlesQuery(t12);
  if (isLoading && !data) {
    let t13;
    if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
      t13 = <ArticleListProfoundSkeleton />;
      $[16] = t13;
    } else {
      t13 = $[16];
    }
    return t13;
  }
  const article = data?.items?.[0];
  if (!article) {
    return null;
  }
  let t13;
  if ($[17] !== article) {
    t13 = extractArgsSearchResult(article);
    $[17] = article;
    $[18] = t13;
  } else {
    t13 = $[18];
  }
  const cardProps = t13;
  let t14;
  if ($[19] !== cardProps || $[20] !== seoLevel) {
    t14 = <section className="component component--no-header"><div className="container"><FeaturedArticleCard {...cardProps} seoLevel={seoLevel} /></div></section>;
    $[19] = cardProps;
    $[20] = seoLevel;
    $[21] = t14;
  } else {
    t14 = $[21];
  }
  return t14;
};
export const ArticleListProfoundProvider: React.FC<BlockProps> = props => {
  const $ = _c(3);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <ArticleListProfoundSkeleton />;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] !== props) {
    t1 = <Suspense fallback={t0}><QueryClientProvider client={queryClient}><ArticleListProfound {...props} /></QueryClientProvider></Suspense>;
    $[1] = props;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return t1;
};